import React, { useState, useEffect } from "react";
import useAutoTour from '../../tours/useAutoTour';
import useChatIntro from '../../tours/useChatIntro';
import InvoicingDashboard from "../InvoicingDashboard";
import ExpenseList from "./ExpenseList";
import TaxSummary from "./TaxSummary";
import ProjectFinancials from "../Projects/ProjectFinancials";
import TransactionsList from "./TransactionsList";
import RecurringInvoices from "./RecurringInvoices";
import { PageLayout, PageHeader, TabGroup, TabPanel, Skeleton } from '../../ui';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { featureAllowed } from '../../utils/planRule';
import './invoicing.css';
const PurchaseOrders = React.lazy(() => import("../PurchaseOrders/PurchaseOrders"));

// ─── Sub-navigation items ──────────────────────────────────────────────────────
// ⏳ TEMPORARY, PENDING BLOCK 240 — NOT A SCOPE DECISION.
// Retainer billing is how Aidey's customers get paid; shipping without it is a
// competitive gap, so this feature is being FIXED and restored, not dropped.
// Restore the line at the bottom of this comment once Block 240 lands and has
// been tested end to end. Do not let "hidden" quietly become "cut".
//
// Block 240: "Recurring invoices" is deliberately ABSENT from this list.
// The feature has never run in production (zero templates, zero generated
// invoices), and it cannot produce a correct invoice as built: a recurring
// line item has no `tax` key at all, so the generated invoice is un-taxable
// by construction while the modal shows the owner a tax line and a gross
// total. Generate-now also marks an invoice 'sent' without emailing anything.
// The routes and <RecurringInvoices /> stay live and harmless — nothing calls
// them while this entry is gone. RESTORING IT IS THIS ONE LINE:
//   { id: "recurring",       label: "Recurring invoices", color: "pastel-mint" },
//
// Block K (UI-22): the sections are the page's tab group (each its own pastel), in place of the old
// Settings-style left rail and the phone's <select>. The tour marks stay on the same things: the
// group is "invoicing-nav" and each tab "invoicing-nav-<id>".
//
// UI-24 item 19, decision 1 (Carla, 2026-10-20): Basics gets INVOICES ONLY. Each section names the
// feature it is (utils/planRule FEATURE_TIER, the mirror of lib/planGates); a plan without it does
// not see its tab, and is told in one line what Pro adds. The server refuses those writes to Basics.
const NAV_ITEMS = [
  { id: "invoices",        label: "Invoices",        color: "pastel-green",  feature: "invoicing" },
  { id: "purchase-orders", label: "Purchase orders", color: "pastel-blue",   feature: "purchaseOrders" },
  { id: "expenses",        label: "Expenses",        color: "pastel-orange", feature: "expenses" },
  { id: "vat",             label: "Tax summary",     color: "pastel-yellow", feature: "taxSummary" },
  { id: "transactions",    label: "Transactions",    color: "pastel-lilac",  feature: "transactions" },
];
const UPGRADE_URL = "/settings?section=billing";   // Plan & billing, as the Send refusal links

const SUBLINES = {
  invoices: "Bill clients, get paid, and see what is still owed.",
  "purchase-orders": "Purchase orders you send to suppliers, with a link they can open.",
  expenses: "What the business spends, with receipts, ready to bill on.",
  vat: "Tax collected on invoices and paid on expenses, by period.",
  transactions: "Money in and money out, in one list.",
  recurring: "Invoices that repeat on a schedule.",
};

// ─── Layout ───────────────────────────────────────────────────────────────────
function InvoicingLayout({ externalOpenFormWithProjectId, onExternalFormOpened, externalBillTimeEntries, onBillTimeOpened }) {
  useAutoTour('invoicing');
  useChatIntro('invoicing');
  const [section, setSection] = useState("invoices");
  const [financialProjectId, setFinancialProjectId] = useState(null);
  const [openFormWithProjectId, setOpenFormWithProjectId] = useState(null);
  const [billTimeEntries, setBillTimeEntries] = useState(null);
  const { currentUser } = useCurrentUser();
  const navItems = NAV_ITEMS.filter(item => featureAllowed(currentUser, item.feature));
  const lockedSome = navItems.length < NAV_ITEMS.length;
  // A section the plan does not include is never drawn, whatever set it (Recurring has no tab, and
  // is part of invoicing).
  const sectionAllowed = section === "recurring" || navItems.some(item => item.id === section);

  // The tour marks each section tab. TabGroup (shared) takes no per-tab attributes, so they are set
  // on the rendered tabs by id rather than changing the shared component.
  useEffect(() => {
    navItems.forEach(item => {
      document.getElementById(`invoicing-tab-${item.id}`)?.setAttribute('data-tour-id', `invoicing-nav-${item.id}`);
    });
  });

  useEffect(() => {
    if (externalOpenFormWithProjectId != null) {
      setFinancialProjectId(null);
      setSection("invoices");
      setOpenFormWithProjectId(externalOpenFormWithProjectId);
      onExternalFormOpened?.();
    }
  }, [externalOpenFormWithProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalBillTimeEntries != null) {
      setFinancialProjectId(null);
      setSection("invoices");
      setBillTimeEntries(externalBillTimeEntries);
      onBillTimeOpened?.();
    }
  }, [externalBillTimeEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleOpenProjectFinancials(projectId) {
    setFinancialProjectId(projectId);
  }

  function handleNewInvoiceForProject(projectId) {
    setFinancialProjectId(null);
    setSection("invoices");
    setOpenFormWithProjectId(projectId);
  }

  function handleFinancialsBack() {
    setFinancialProjectId(null);
  }

  function chooseSection(id) {
    setFinancialProjectId(null);
    setSection(id);
  }

  function renderContent() {
    if (financialProjectId != null) {
      return (
        <ProjectFinancials
          projectId={financialProjectId}
          onBack={handleFinancialsBack}
          onNewInvoice={handleNewInvoiceForProject}
        />
      );
    }
    if (!sectionAllowed) return null;
    switch (section) {
      case "invoices":        return <InvoicingDashboard onOpenProjectFinancials={handleOpenProjectFinancials} openFormWithProjectId={openFormWithProjectId} onFormOpened={() => setOpenFormWithProjectId(null)} billTimeEntries={billTimeEntries} onBillTimeFormOpened={() => setBillTimeEntries(null)} />;
      case "recurring":       return <RecurringInvoices />;
      case "purchase-orders": return <React.Suspense fallback={<Skeleton lines={4} label="Loading purchase orders" />}><PurchaseOrders /></React.Suspense>;
      case "expenses":        return <ExpenseList />;
      case "vat":             return <TaxSummary />;
      case "transactions":    return <TransactionsList />;
      default:                return null;
    }
  }

  return (
    <PageLayout data-page="invoicing">
      <PageHeader
        eyebrow={{ label: 'Business workspace', color: 'group-clients' }}
        title="Invoicing"
        subline={SUBLINES[section]}
        help="invoicing"
      />
      {navItems.length > 1 && (
        <nav data-tour-id="invoicing-nav" className="invc-nav" aria-label="Invoicing sections">
          <TabGroup tabs={navItems} value={section} onChange={chooseSection} label="Invoicing sections" idPrefix="invoicing" />
        </nav>
      )}
      {lockedSome && (
        <p className="invc-muted invc-pro-tools" data-testid="invoicing-pro-tools">
          Purchase orders, expenses, tax summary, transactions and Xero come with Pro. <a href={UPGRADE_URL}>See plans</a>
        </p>
      )}
      {navItems.length > 1 ? (
        <TabPanel idPrefix="invoicing" tabId={section}>
          <div className="invc-section">{renderContent()}</div>
        </TabPanel>
      ) : (
        <div className="invc-section">{renderContent()}</div>
      )}
    </PageLayout>
  );
}

export default InvoicingLayout;
